import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  OverviewPageSearchType,
  TaskProgressDisplayType,
  TaskProgressDisplayFormat,
  TaskProgressIndicatorType,
  TaskProgressColorMap,
  UiModuleOverviewConfiguration,
} from '@seahorse/domain';

/** One option of a select of the UI module configuration forms. */
export interface ConfigurationOption<T = string | number> {
  label: string;
  value: T;
}

/** The type of one summary action of an overview configuration. */
export type OverviewSummaryAction = NonNullable<
  UiModuleOverviewConfiguration['summaryConfig']['actions']
>[number];

export const createSearchOptions: () => ConfigurationOption[] = () => {
  const translateService = inject(TranslateService);

  return [
    {
      value: OverviewPageSearchType.SimpleSearch,
      label: translateService.instant('shared.terms.simpleSearch'),
    },
    {
      value: OverviewPageSearchType.AdvancedSearch,
      label: translateService.instant('shared.terms.advancedSearch'),
    },
  ];
};

export const createTaskProgressDisplayTypeOptions: () => ConfigurationOption<TaskProgressDisplayType>[] =
  () => {
    const translateService = inject(TranslateService);

    return [
      {
        value: TaskProgressDisplayType.AllTasks,
        label: translateService.instant(
          'uiModule.taskProgress.displayType.allTasks'
        ),
      },
      {
        value: TaskProgressDisplayType.ColumnPerTaskGroup,
        label: translateService.instant(
          'uiModule.taskProgress.displayType.columnPerTaskGroup'
        ),
      },
    ];
  };

export const createTaskProgressDisplayFormatOptions: () => ConfigurationOption<TaskProgressDisplayFormat>[] =
  () => {
    const translateService = inject(TranslateService);

    return [
      {
        value: TaskProgressDisplayFormat.Percentage,
        label: translateService.instant(
          'uiModule.taskProgress.displayFormat.percentage'
        ),
      },
      {
        value: TaskProgressDisplayFormat.Absolute,
        label: translateService.instant(
          'uiModule.taskProgress.displayFormat.absolute'
        ),
      },
    ];
  };

export const createTaskProgressIndicatorTypeOptions: () => ConfigurationOption<TaskProgressIndicatorType>[] =
  () => {
    const translateService = inject(TranslateService);
    return [
      {
        value: TaskProgressIndicatorType.ProgressBar,
        label: translateService.instant(
          'uiModule.taskProgress.indicatorType.progressBar'
        ),
      },
      {
        value: TaskProgressIndicatorType.PlainText,
        label: translateService.instant(
          'uiModule.taskProgress.indicatorType.plainText'
        ),
      },
    ];
  };

export const DEFAULT_PROGRESS_COLOR_MAP: TaskProgressColorMap[] = [
  { boundary: 0, color: '#ff0000' },
  { boundary: 50, color: '#ffff00' },
  { boundary: 90, color: '#00ff00' },
];

/**
 * Both required fields of an overview configuration are filled in: the page
 * size and the sort field code. Hosts use it to enable their save button
 * (it works on the data, so it does not depend on which section is shown).
 */
export function isOverviewConfigurationValid(
  configuration: Pick<
    UiModuleOverviewConfiguration,
    'pageSize' | 'sortFieldCode'
  > | null
): boolean {
  return (
    configuration?.pageSize !== null &&
    configuration?.pageSize !== undefined &&
    !!configuration?.sortFieldCode?.trim()
  );
}
